/**
 * El intercambio OIDC y el refresh viven en el GraphQL `/public` de la
 * PLATAFORMA (api.tbit.app), NO en el API de job-apply (api.jobs.tbit.app):
 * job-apply-service no emite tokens, sólo verifica los que la plataforma firmó.
 * Por eso esta base es distinta a la de `lib/api.ts`.
 */
function platformApiBase(): string {
  const explicit = import.meta.env.VITE_PLATFORM_API_BASE_URL;
  if (explicit !== undefined) return explicit;
  // VITE_API_BASE_URL definida VACÍA es el modo mismo-origen del dev: ahí
  // `/public` lo proxea vite.config.ts y ponerle host lo sacaría del proxy.
  if (import.meta.env.VITE_API_BASE_URL === '') return '';
  return 'https://api.tbit.app';
}

const GRAPHQL_PUBLIC_URL = `${platformApiBase()}/public`;

export async function exchangeOidcToken(idToken: string): Promise<{ accessToken: string; refreshToken: string }> {
  const res = await fetch(GRAPHQL_PUBLIC_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      query: `
        mutation oidc_token_exchange($data: OidcTokenExchangeInputType!) {
          oidc_token_exchange(data: $data) {
            hasError
            message
            data
          }
        }
      `,
      variables: {
        data: { id_token: idToken },
      },
    }),
  });

  const json = await res.json();
  const result = json?.data?.oidc_token_exchange;

  if (result?.hasError) {
    throw new Error(result.message || 'Token exchange failed');
  }

  return {
    accessToken: result.data.accessToken,
    refreshToken: result.data.refreshToken,
  };
}

export function setAuthCookie(accessToken: string) {
  document.cookie = `accessToken=${accessToken}; path=/; max-age=${60 * 60 * 24}; SameSite=Lax`;
}

// The refresh token outlives the access token (30d server-side) so an expired
// access token can be renewed without sending the user back through OIDC.
export function setRefreshCookie(refreshToken: string) {
  document.cookie = `refreshToken=${refreshToken}; path=/; max-age=${60 * 60 * 24 * 30}; SameSite=Lax`;
}

export function clearAuthCookie() {
  document.cookie = 'accessToken=; path=/; max-age=0';
  document.cookie = 'refreshToken=; path=/; max-age=0';
}

function readCookie(name: string): string | null {
  const cookie = document.cookie.split('; ').find((c) => c.startsWith(`${name}=`));
  return cookie ? cookie.split('=')[1] : null;
}

export function getAccessToken(): string | null {
  return readCookie('accessToken');
}

export function getRefreshToken(): string | null {
  return readCookie('refreshToken');
}

/**
 * Exchanges the stored refresh token for a fresh access token and persists both.
 * Returns the new access token, or null when there is nothing to refresh with or
 * the server rejects the token (revoked/expired) — callers should then re-login.
 *
 * Concurrent callers share one in-flight request: a page that fires several
 * requests at once must not burn the refresh token N times, since each refresh
 * rotates it server-side and the losers would send an already-rotated token.
 */
let inFlightRefresh: Promise<string | null> | null = null;

export function refreshAccessToken(): Promise<string | null> {
  if (inFlightRefresh) return inFlightRefresh;

  const refreshToken = getRefreshToken();
  if (!refreshToken) return Promise.resolve(null);

  const run = (async () => {
    try {
      const res = await fetch(GRAPHQL_PUBLIC_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: `
            mutation refresh_token($data: RefreshTokenInputType!) {
              refresh_token(data: $data) {
                hasError
                message
                data
              }
            }
          `,
          variables: { data: { refresh_token: refreshToken } },
        }),
      });

      const json = await res.json();
      const result = json?.data?.refresh_token;
      if (result?.hasError || !result?.data?.accessToken) {
        clearAuthCookie();
        return null;
      }

      setAuthCookie(result.data.accessToken);
      if (result.data.refreshToken) setRefreshCookie(result.data.refreshToken);
      return result.data.accessToken as string;
    } catch {
      // Network failure is not proof the token is bad — leave cookies alone so a
      // transient blip doesn't log the user out.
      return null;
    }
  })();

  // Cleared outside the async body: an early return there would run before the
  // assignment below and pin a stale resolved promise here forever.
  inFlightRefresh = run.finally(() => {
    inFlightRefresh = null;
  });

  return inFlightRefresh;
}

export function isAuthenticated(): boolean {
  return !!getAccessToken();
}

export function isEmbedded(): boolean {
  return typeof window !== 'undefined' && window.parent !== window;
}

export const SESSION_EXPIRED_EVENT = 'tbit-session-expired';

/**
 * Obtains a usable access token after one was rejected. This client is always a
 * top-level page holding its own refresh token, so that is the only avenue —
 * web-exapps also had a postMessage handshake with a host frame, which was for
 * the embedded SoluAgro portal and has no counterpart here.
 *
 * Returns null when the session is truly unrecoverable.
 */
export async function renewSession(): Promise<string | null> {
  return refreshAccessToken();
}

/**
 * Announces that the session is beyond recovery so the UI can offer a way out
 * instead of surfacing a bare API error.
 */
export function notifySessionExpired() {
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
}
